import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Authentication Store
 * Manages user authentication state across the application
 *
 * NOTE: No localStorage persistence - server (omni_session cookie) is the
 * authoritative source of authentication. usePassportSSO hook validates
 * session on page load and populates this store.
 */
const useAuthStore = create(
  devtools(
    (set, get) => ({
      // ========== STATE ==========
      user: null,
      isAuthenticated: false,
      isLoading: true,
      walletAddress: null,
      isAutoConnecting: false,
      walletTimeout: false,

      // ========== ACTIONS ==========

      /**
       * Set user data and authentication status
       * @param {Object|null} user - User object or null to clear
       */
      setUser: (user) =>
        set(
          {
            user,
            isAuthenticated: !!user,
            isLoading: false,
            walletAddress: user?.wallet || user?.creator || null,
          },
          false,
          'auth/setUser'
        ),

      /**
       * Login user
       * @param {Object} user - User data from API
       */
      login: (user) =>
        set(
          {
            user,
            isAuthenticated: true,
            isLoading: false,
            walletAddress: user?.wallet || user?.creator,
            isAutoConnecting: false,
          },
          false,
          'auth/login'
        ),

      /**
       * Logout user and clear all auth data
       */
      logout: () => {
        // Clear state
        set(
          {
            user: null,
            isAuthenticated: false,
            isLoading: false,
            walletAddress: null,
            isAutoConnecting: false,
            walletTimeout: false,
          },
          false,
          'auth/logout'
        );

        // Clear any legacy localStorage
        localStorage.removeItem('auth-storage');
        localStorage.removeItem('currentUser');
        localStorage.removeItem('authToken');
      },

      /**
       * Update user data partially
       * @param {Object} updates - Partial user object with fields to update
       */
      updateUser: (updates) =>
        set(
          (state) => ({
            user: state.user ? { ...state.user, ...updates } : null,
          }),
          false,
          'auth/updateUser'
        ),

      /**
       * Set loading state
       * @param {boolean} loading - Loading state
       */
      setLoading: (loading) =>
        set({ isLoading: loading }, false, 'auth/setLoading'),

      /**
       * Update wallet address
       * @param {string|null} address - Wallet address
       */
      updateWalletAddress: (address) => {
        set(
          {
            walletAddress: address,
            isAutoConnecting: false,
          },
          false,
          'auth/updateWalletAddress'
        );

        // If user is logged in, sync wallet address
        const currentUser = get().user;
        if (currentUser && address && address !== currentUser.wallet) {
          set(
            {
              user: { ...currentUser, wallet: address },
            },
            false,
            'auth/syncWalletAddress'
          );
        }
      },

      /**
       * Set wallet timeout flag (prevents infinite loading)
       */
      setWalletTimeout: (timeout) =>
        set(
          {
            walletTimeout: timeout,
            isAutoConnecting: !timeout,
          },
          false,
          'auth/setWalletTimeout'
        ),

      /**
       * Check if user is waiting for wallet connection
       * @returns {boolean}
       */
      isWaitingForWallet: () => {
        const state = get();
        return (
          state.isAutoConnecting ||
          (!state.walletAddress && !state.walletTimeout)
        );
      },

      /**
       * Reset auth state (useful for testing)
       */
      reset: () =>
        set(
          {
            user: null,
            isAuthenticated: false,
            isLoading: true,
            walletAddress: null,
            isAutoConnecting: false,
            walletTimeout: false,
          },
          false,
          'auth/reset'
        ),
    }),
    {
      name: 'AuthStore',
      enabled: import.meta.env.DEV,
    }
  )
);

// ========== SELECTORS ==========
// Use these for optimized component re-renders

export const selectUser = (state) => state.user;
export const selectIsAuthenticated = (state) => state.isAuthenticated;
export const selectIsLoading = (state) => state.isLoading;
export const selectWalletAddress = (state) => state.walletAddress;
export const selectWalletTimeout = (state) => state.walletTimeout;
export const selectUsername = (state) =>
  state.user?.username ||
  state.user?.name ||
  (state.user?.wallet ? `${state.user.wallet.slice(0, 6)}...` : null);
export const selectUserRole = (state) => state.user?.role;
export const selectIsAdmin = (state) => state.user?.role === 'admin';
export const selectIsModerator = (state) =>
  state.user?.role === 'admin' || state.user?.role === 'moderator';
export const selectUserOid = (state) => state.user?.oid;
export const selectUserAvatar = (state) => state.user?.avatar;
export const selectIsWaitingForWallet = (state) =>
  state.isAutoConnecting || (!state.walletAddress && !state.walletTimeout);

export default useAuthStore;
